import { createContext, useContext } from 'react';

export interface MenuState {
  containerClassnames: string;
  menuClickCount: number;
  selectedMenuHasSubItems: boolean;
}

export type MenuAction =
  | { type: 'SET_CONTAINER_CLASSNAMES'; payload: { clickCount: number; classnames: string; hasSubItems: boolean } }
  | { type: 'CLICK_MOBILE_MENU'; payload: { classnames: string; clickCount: number } }
  | { type: 'ADD_CONTAINER_CLASSNAME'; payload: { classname: string } }
  | { type: 'CHANGE_DEFAULT_CLASSNAMES'; payload: { classnames: string } }
  | { type: 'CHANGE_SELECTED_MENU_HAS_SUB_ITEMS'; payload: { hasSubItems: boolean } }
  | { type: 'TOGGLE_MENU' };

export const initialState: MenuState = {
  containerClassnames: 'menu-default sub-hidden',
  menuClickCount: 0,
  selectedMenuHasSubItems: false,
};

export const menuReducer = (state: MenuState, action: MenuAction): MenuState => {
  switch (action.type) {
    case 'SET_CONTAINER_CLASSNAMES':
      return {
        ...state,
        menuClickCount: action.payload.clickCount,
        containerClassnames: action.payload.classnames,
        selectedMenuHasSubItems: action.payload.hasSubItems,
      };
    case 'CLICK_MOBILE_MENU':
      return { ...state, containerClassnames: action.payload.classnames, menuClickCount: action.payload.clickCount };
    case 'ADD_CONTAINER_CLASSNAME':
      return { ...state, containerClassnames: `${state.containerClassnames} ${action.payload.classname}` };
    case 'CHANGE_DEFAULT_CLASSNAMES':
      return { ...state, containerClassnames: action.payload.classnames };
    case 'CHANGE_SELECTED_MENU_HAS_SUB_ITEMS':
      return { ...state, selectedMenuHasSubItems: action.payload.hasSubItems };
    case 'TOGGLE_MENU': {
      const currentClasses = state.containerClassnames.split(' ').filter(Boolean);
      const isMobile = currentClasses.includes('menu-mobile');
      let nextClassesArray;

      if (isMobile) {
        const isShown = currentClasses.includes('main-show-temporary');
        if (isShown) {
          nextClassesArray = currentClasses.filter((c) => c !== 'main-show-temporary');
        } else {
          nextClassesArray = [...currentClasses, 'main-show-temporary'];
        }
      } else {
        const isHidden = currentClasses.includes('main-hidden');
        if (isHidden) {
          nextClassesArray = currentClasses.filter((c) => c !== 'main-hidden');
        } else {
          nextClassesArray = [...currentClasses, 'main-hidden', 'sub-hidden'];
        }
      }

      return {
        ...state,
        containerClassnames: [...new Set(nextClassesArray)].join(' '),
        menuClickCount: 0,
      };
    }
    default:
      return state;
  }
};

export const MenuContext = createContext<
  | (MenuState & {
      setContainerClassnames: (clickCount: number, classnames: string, hasSubItems: boolean) => void;
      clickOnMobileMenu: (classnames: string, clickCount: number) => void;
      addContainerClassname: (classname: string) => void;
      changeDefaultClassnames: (classnames: string) => void;
      changeSelectedMenuHasSubItems: (hasSubItems: boolean) => void;
      toggleMenu: () => void;
    })
  | undefined
>(undefined);

export const useMenu = (): Required<typeof MenuContext extends React.Context<infer T | undefined> ? T : never> => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('useMenu must be used within a MenuProvider');
  }
  return context;
};
